import type { Editor } from "@tiptap/core";

interface IEmailBuilderRichTextSession {
  blockId: string;
  editor: Editor;
  readStoredHtml: () => string;
}

type TEmailBuilderRichTextCaretPoint = {
  blockId: string;
  x: number;
  y: number;
};

let activeSession: IEmailBuilderRichTextSession | null = null;
let pendingCaret: TEmailBuilderRichTextCaretPoint | null = null;
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach(listener => listener());
}

export function subscribeEmailBuilderRichTextSession(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function registerEmailBuilderRichTextSession(session: IEmailBuilderRichTextSession): void {
  activeSession = session;
  notify();
}

export function unregisterEmailBuilderRichTextSession(blockId: string): void {
  if (activeSession?.blockId !== blockId) {
    return;
  }

  activeSession = null;
  notify();
}

export function getEmailBuilderRichTextEditor(blockId: string): Editor | null {
  if (activeSession?.blockId !== blockId) {
    return null;
  }

  return activeSession.editor;
}

export function emailBuilderRichTextSessionBlockId(editor: Editor): string | null {
  if (activeSession?.editor !== editor) {
    return null;
  }

  return activeSession.blockId;
}

export function readEmailBuilderRichTextStoredHtml(blockId: string): string | null {
  if (activeSession?.blockId !== blockId) {
    return null;
  }

  return activeSession.readStoredHtml();
}

export function setEmailBuilderRichTextCaretPoint(blockId: string, point: { x: number; y: number }): void {
  pendingCaret = { blockId, x: point.x, y: point.y };
}

export function takeEmailBuilderRichTextCaretPoint(blockId: string): { x: number; y: number } | null {
  if (pendingCaret?.blockId !== blockId) {
    return null;
  }

  const point = { x: pendingCaret.x, y: pendingCaret.y };
  pendingCaret = null;
  return point;
}

export function placeEmailBuilderRichTextCaret(blockId: string, x: number, y: number): boolean {
  const editor = getEmailBuilderRichTextEditor(blockId);
  if (editor === null) {
    return false;
  }

  const position = editor.view.posAtCoords({ left: x, top: y });
  if (position === null) {
    return false;
  }

  editor.chain().focus().setTextSelection(position.pos).run();
  return true;
}

export function toggleEmailBuilderRichTextMergeTagBrackets(blockId: string): void {
  const editor = getEmailBuilderRichTextEditor(blockId);
  if (editor === null) {
    return;
  }

  const context = textblockCaretContext(editor);
  if (context !== null) {
    const existing = completedMergeTagRange(context);
    if (existing !== null) {
      editor.chain().focus().deleteRange(existing).run();
      return;
    }
  }

  editor.chain().focus().insertContent("{{}}").run();
  const caret = editor.state.selection.from - 2;
  if (caret > 0) {
    editor.commands.setTextSelection(caret);
  }
}

interface ITextblockCaretContext {
  textBefore: string;
  textAfter: string;
  pos: number;
}

export function readEmailBuilderRichTextCaretContext(editor: Editor): ITextblockCaretContext | null {
  return textblockCaretContext(editor);
}

export function emailBuilderRichTextCaretInMergeTag(editor: Editor): boolean {
  const context = textblockCaretContext(editor);
  return context !== null && completedMergeTagRange(context) !== null;
}

function textblockCaretContext(editor: Editor): ITextblockCaretContext | null {
  const { $from } = editor.state.selection;
  if (!$from.parent.isTextblock) {
    return null;
  }

  const offset = $from.parentOffset;
  return {
    textBefore: $from.parent.textBetween(0, offset, "\n", "\n"),
    textAfter: $from.parent.textBetween(offset, $from.parent.content.size, "\n", "\n"),
    pos: $from.pos,
  };
}

function completedMergeTagRange(context: ITextblockCaretContext): { from: number; to: number } | null {
  const text = context.textBefore + context.textAfter;
  const caret = context.textBefore.length;
  const pattern = /\{\{[^}]+\}\}/g;
  let match: RegExpExecArray | null = pattern.exec(text);

  while (match !== null) {
    const start = match.index;
    const end = start + match[0].length;
    if (caret >= start && caret <= end) {
      return {
        from: context.pos - (caret - start),
        to: context.pos + (end - caret),
      };
    }

    match = pattern.exec(text);
  }

  return null;
}
